import type { SVGProps } from 'react'

/** Thin-stroke (1.5) icon set drawn on a 24px grid. Kept in-house so every glyph shares one weight. */
type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 16, children, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  )
}

export const ArrowRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Svg>
)
export const ArrowUpRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 17 17 7M8 7h9v9" />
  </Svg>
)
export const ArrowLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Svg>
)
export const ChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="m15 6-6 6 6 6" />
  </Svg>
)
export const ChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9 6 6 6-6 6" />
  </Svg>
)
export const ChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
)
export const Search = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Svg>
)
export const Refresh = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 12a8 8 0 1 1-2.34-5.66" />
    <path d="M20 4v5h-5" />
  </Svg>
)
export const Download = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4v11M7 10l5 5 5-5M4 19h16" />
  </Svg>
)
export const Android = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 9.5a5 5 0 0 1 10 0V17a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1z" />
    <path d="M8.5 6.5 7 4M15.5 6.5 17 4M5 11v4M19 11v4M9.5 18v2.5M14.5 18v2.5" />
    <circle cx="10" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="14" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
  </Svg>
)
export const Plus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)
export const Close = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
)
export const Check = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Svg>
)
export const Warning = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5 2.5 20h19L12 3.5Z" />
    <path d="M12 10v4M12 17.2v.3" />
  </Svg>
)
export const Info = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 8v.3" />
  </Svg>
)
export const MapPin = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
    <circle cx="12" cy="10" r="2.25" />
  </Svg>
)
export const Radar = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
  </Svg>
)
export const Users = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9.5" cy="8" r="3.25" />
    <path d="M3.5 19.5a6 6 0 0 1 12 0" />
    <path d="M15.5 4.9a3.25 3.25 0 0 1 0 6.2M17.2 14.2a6 6 0 0 1 3.3 5.3" />
  </Svg>
)
export const Clock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Svg>
)
export const Phone = (p: IconProps) => (
  <Svg {...p}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
    <path d="M10.5 18.5h3" />
  </Svg>
)
export const Tablet = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4.5" y="3" width="15" height="18" rx="2.5" />
    <path d="M10.5 18h3" />
  </Svg>
)
export const Building = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 21V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V21M14 10h4.5A1.5 1.5 0 0 1 20 11.5V21M3 21h18" />
    <path d="M7.5 8h3M7.5 12h3M7.5 16h3M17 14h.5M17 18h.5" />
  </Svg>
)
export const Calendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Svg>
)
export const LogOut = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10M15 8l4 4-4 4M19 12H9" />
  </Svg>
)
export const Globe = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17M12 3.5c2.5 2.6 3.7 5.4 3.7 8.5s-1.2 5.9-3.7 8.5c-2.5-2.6-3.7-5.4-3.7-8.5S9.5 6.1 12 3.5Z" />
  </Svg>
)
export const Pencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="m4 20 4.2-.9L19.4 7.9a1.6 1.6 0 0 0 0-2.3l-1-1a1.6 1.6 0 0 0-2.3 0L4.9 15.8 4 20Z" />
    <path d="m14.5 6.2 3.3 3.3" />
  </Svg>
)
export const Copy = (p: IconProps) => (
  <Svg {...p}>
    <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
    <path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
  </Svg>
)
export const Link = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.2 1.2" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.2-1.2" />
  </Svg>
)
export const Power = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5v8M6.6 7.2a7.5 7.5 0 1 0 10.8 0" />
  </Svg>
)
export const Sparkle = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.2 2.2M15.5 15.5l2.2 2.2M6.3 17.7l2.2-2.2M15.5 8.5l2.2-2.2" />
  </Svg>
)
export const Inbox = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 13.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3.5" />
    <path d="M4 13.5 6.2 6.6A1.5 1.5 0 0 1 7.6 5.5h8.8a1.5 1.5 0 0 1 1.4 1.1L20 13.5H15.5a3.5 3.5 0 0 1-7 0H4Z" />
  </Svg>
)
export const Calculator = (p: IconProps) => (
  <Svg {...p}>
    <rect x="5" y="3" width="14" height="18" rx="2.5" />
    <path d="M8.5 7h7M8.5 12h.3M12 12h.3M15.5 12h.3M8.5 16h.3M12 16h.3M15.5 16h.3" />
  </Svg>
)
export const Eye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.5 12S5.8 5.5 12 5.5 21.5 12 21.5 12 18.2 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
)
export const EyeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.6A9.9 9.9 0 0 1 12 5.5c6.2 0 9.5 6.5 9.5 6.5a15.6 15.6 0 0 1-3.4 4.3M6.5 7.2A15.6 15.6 0 0 0 2.5 12S5.8 18.5 12 18.5a9.6 9.6 0 0 0 4-.85" />
    <path d="M9.9 10a3 3 0 0 0 4.15 4.13" />
  </Svg>
)
export const Shield = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5l7 3v5.2c0 4.6-3 7.9-7 8.8-4-0.9-7-4.2-7-8.8V6.5l7-3Z" />
    <path d="M9 12l2 2 4-4" />
  </Svg>
)
export const FileText = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5M9 13h6M9 17h6" />
  </Svg>
)
export const Trash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
  </Svg>
)
export const Bluetooth = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 7l10 10-5 4V3l5 4L7 17" />
  </Svg>
)
export const Thermometer = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 13.5V5a2 2 0 0 1 4 0v8.5a3.5 3.5 0 1 1-4 0Z" />
    <path d="M12 10v6" />
  </Svg>
)
export const Droplet = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5s6 6.2 6 10.5a6 6 0 0 1-12 0c0-4.3 6-10.5 6-10.5Z" />
  </Svg>
)
export const Package = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
    <path d="M4 7.5l8 4.5 8-4.5M12 12v9M8 5.25l8 4.5" />
  </Svg>
)
export const Apple = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15.5 4.5c-.2 1.3-1.1 2.4-2.3 2.6.1-1.3 1-2.4 2.3-2.6Z" fill="currentColor" stroke="none" />
    <path d="M16.6 12.6c0-2 1.6-2.9 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.4-.4 6 1 8 .7 1 1.5 2 2.5 2s1.4-.6 2.6-.6c1.2 0 1.5.6 2.6.6s1.7-1 2.4-2c.7-1.1 1-2.1 1.1-2.2 0 0-2.1-.8-2.1-3.2Z" />
  </Svg>
)
export const MessageCircle = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 12a8.5 8.5 0 0 1-12.6 7.4L4 21l1.6-4.2A8.5 8.5 0 1 1 21 12Z" />
  </Svg>
)
export const Bell = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </Svg>
)
export const Percent = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 5L5 19" />
    <circle cx="7" cy="7" r="2.25" />
    <circle cx="17" cy="17" r="2.25" />
  </Svg>
)
export const Menu = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
)
export const Paperclip = (p: IconProps) => (
  <Svg {...p}>
    <path d="m20 11.5-7.8 7.8a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />
  </Svg>
)
export const Image = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
    <circle cx="9" cy="9.5" r="1.5" />
    <path d="M20.5 15.5 15.5 11l-6 6.5M12 14.5l-2-2-6 5" />
  </Svg>
)
export const Reply = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 7 4 12l5 5" />
    <path d="M4 12h9a7 7 0 0 1 7 7v1" />
  </Svg>
)
export const Forward = (p: IconProps) => (
  <Svg {...p}>
    <path d="m15 7 5 5-5 5" />
    <path d="M20 12h-9a7 7 0 0 0-7 7v1" />
  </Svg>
)
export const Smile = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 14a4.5 4.5 0 0 0 7 0" />
    <path d="M9.5 9.5h.01M14.5 9.5h.01" strokeWidth={2} />
  </Svg>
)
export const CheckDouble = (p: IconProps) => (
  <Svg {...p}>
    <path d="m2.5 12.5 4 4L14 9" />
    <path d="m11 16.5 1.5 1.5L21 9.5" />
  </Svg>
)
export const CloudRain = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.5 14.5a4 4 0 0 1-.4-7.98 5.5 5.5 0 0 1 10.6 1.5A3.5 3.5 0 0 1 17 14.5H6.5Z" />
    <path d="M8 17.5 7 20M12 17.5 11 20M16 17.5 15 20" />
  </Svg>
)
export const BellOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
    <path d="M4 4l16 16" />
  </Svg>
)
